import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TenantContext = createContext(null);

export function TenantProvider({ children }) {
  const { user } = useAuth();
  const memberships = useMemo(() => user?.memberships || [], [user]);
  const [companyId, setCompanyId] = useState(() => localStorage.getItem("esg_company_id"));
  const [periodId, setPeriodId] = useState(() => localStorage.getItem("esg_period_id"));
  const [periods, setPeriods] = useState([]);

  const membership = useMemo(
    () => memberships.find((m) => m.company_id === companyId) || null,
    [memberships, companyId]
  );

  useEffect(() => {
    if (!memberships.length) return;
    if (!memberships.find((m) => m.company_id === companyId)) {
      const first = memberships[0].company_id;
      setCompanyId(first);
      localStorage.setItem("esg_company_id", first);
    }
  }, [memberships, companyId]);

  const changeCompany = useCallback((id) => {
    setCompanyId(id);
    localStorage.setItem("esg_company_id", id);
    setPeriodId(null);
    localStorage.removeItem("esg_period_id");
  }, []);

  const changePeriod = useCallback((id) => {
    setPeriodId(id);
    localStorage.setItem("esg_period_id", id);
  }, []);

  const refreshPeriods = useCallback(async () => {
    if (!companyId || user?.is_superadmin) {
      setPeriods([]);
      return;
    }
    try {
      const { data } = await api.get(`/companies/${companyId}/periods`);
      setPeriods(data);
    } catch {
      setPeriods([]);
    }
  }, [companyId, user?.is_superadmin]);

  useEffect(() => {
    refreshPeriods();
  }, [refreshPeriods]);

  const period = useMemo(
    () => periods.find((p) => p.id === periodId) || periods[0] || null,
    [periods, periodId]
  );

  // Anagrafica completa dal backend (fonte unica): la membership porta solo id/denominazione.
  const [companyDetail, setCompanyDetail] = useState(null);
  const refreshCompany = useCallback(async () => {
    if (!companyId) { setCompanyDetail(null); return; }
    try {
      const { data } = await api.get(`/companies/${companyId}`);
      setCompanyDetail(data);
    } catch {
      setCompanyDetail(null);
    }
  }, [companyId]);
  useEffect(() => { refreshCompany(); }, [refreshCompany]);

  // Effective capabilities dal backend (STEP A): fonte autorevole per il menu.
  // Mai dal localStorage: ruolo/permessi/moduli arrivano solo dal server.
  const [caps, setCaps] = useState(null);
  useEffect(() => {
    if (!user) { setCaps(null); return; }
    const params = new URLSearchParams();
    if (companyId) params.set("company_id", companyId);
    if (period?.id) params.set("period_id", period.id);
    api.get(`/auth/capabilities?${params.toString()}`)
      .then(({ data }) => setCaps(data))
      .catch(() => setCaps(null));
  }, [user, companyId, period?.id]);

  const can = useCallback(
    (perm) => {
      if (!membership) return false;
      const perms = membership.permissions || [];
      return perms.includes("*") || perms.includes(perm);
    },
    [membership]
  );

  const contextValue = useMemo(() => ({
    memberships,
    membership,
    company: companyDetail && companyDetail.id === companyId
      ? { ...(membership?.company || {}), ...companyDetail }
      : membership?.company || null,
    refreshCompany,
    companyId: membership?.company_id || null,
    changeCompany,
    periods,
    period,
    changePeriod,
    refreshPeriods,
    role: membership?.role || null,
    can,
    caps,
  }), [memberships, membership, companyId, companyDetail, refreshCompany, changeCompany, periods, period, changePeriod, refreshPeriods, can, caps]);

  return (
    <TenantContext.Provider value={contextValue}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  return useContext(TenantContext);
}
